import * as _ from 'lodash';
import moment from 'moment';

import { useToggle } from '../../../../hooks';
import { useProjectReleaseGroups } from '../../../../hooks/api';
import { Badge, Link } from '../../../generic';
import Button, { BUTTON_TYPE } from '../../../generic/Button';
import Loader from '../../../generic/Loader';
import type { ReleaseGroupsForProject } from '../../../../../shared/types/routes/projects';

import projectAffiliationStyles from './ProjectAffiliation.module.less';
import styles from './ReleaseGroups.module.less';

const MAX_RELEASE_TAGS = 5;
const MAX_RELEASE_GROUPS = 10;

/**
 * Component to render the tags for a release group. By default we show a maximum of 5 tags and then
 * show a button to expand the list.
 */
const ReleaseTags = ({ tags } : {
  tags: string[];
}) => {
  const [shouldShowMore, toggleShowMore] = useToggle();
  const extraTagCount = tags.length > MAX_RELEASE_TAGS ? tags.length - MAX_RELEASE_TAGS : 0;
  const normalizedTags = shouldShowMore ? tags : _.take(tags, MAX_RELEASE_TAGS);
  const expandButtonText = shouldShowMore ? 'hide' : `+${extraTagCount} more`;

  return (
    <div className={styles.releases}>
      <span className={styles.tags}>
        {normalizedTags.join(', ')}
      </span>
      {extraTagCount > 0 && (
        <Button type={BUTTON_TYPE.ANCHOR} onClick={toggleShowMore}>
          {expandButtonText}
        </Button>
      )}
    </div>
  );
};

/**
 * Component to render the content of release groups based on data and error state
 */
const ReleaseGroupsContent = ({
  data,
  error,
}: {
  data?: { releaseGroups: ReleaseGroupsForProject[] },
  error: Error | null,
}) => {
  const releaseGroups = data?.releaseGroups || [];
  const visibleReleaseGroups = _.take(releaseGroups, MAX_RELEASE_GROUPS);
  const extraReleaseGroupsCount = releaseGroups.length > MAX_RELEASE_GROUPS ?
    releaseGroups.length - MAX_RELEASE_GROUPS : 0;

  if (error) {
    return (
      <div className={styles.releaseGroups}>
        <div className={styles.releaseGroup}>
          <div className={styles.error}>Error loading release groups</div>
        </div>
      </div>
    );
  }

  if (releaseGroups.length === 0) {
    return (
      <div className={styles.releaseGroups}>
        <div className={styles.releaseGroup}>
          <div className={styles.empty}>This project has no release groups</div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.releaseGroups}>
      {visibleReleaseGroups.map((releaseGroup) => {
        return (
          <div key={releaseGroup.releaseGroupId} className={styles.releaseGroup}>
            <Link variant="primary" to={`/projects/group/${releaseGroup.releaseGroupId}`}>
              <div className={styles.label}>{releaseGroup.releaseGroupTitle}</div>
            </Link>
            <ReleaseTags
              tags={releaseGroup.revisions.flatMap((r) => r.releases.map((release) => release.releaseTitle))}
            />
            <div className={styles.date}>{moment(releaseGroup.createdAt).fromNow()}</div>
          </div>
        );
      })}
      {extraReleaseGroupsCount > 0 && (
        <div className={styles.releaseGroup}>
          <Link to="/release-groups">
            +{extraReleaseGroupsCount} more
          </Link>
        </div>
      )}
    </div>
  );
};

/**
 * Panel UI that shows the release groups for a given project
 */
const ReleaseGroups = ({ projectLocator }: { projectLocator: string }) => {
  const { data, isLoading, error } = useProjectReleaseGroups(projectLocator);

  if (isLoading) {
    return <Loader />;
  }

  const releaseGroups = data?.releaseGroups || [];

  return (
    <div>
      <div className={projectAffiliationStyles.header}>
        <span className={projectAffiliationStyles.label}>Release Groups</span>
        <Badge color="light-gray" shape="circle" size="micro">
          {releaseGroups.length || 0}
        </Badge>
      </div>
      <ReleaseGroupsContent data={data} error={error} />
    </div>
  );
};

export default ReleaseGroups;
